import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";

export interface EmploymentRecord {
  id: number;
  employee_id: number;
  designation_name: string;
  designation_code: string;
  grade_name: string;
  grade_code: string;
  department_name: string;
  start_date: string;
  end_date: string | null;
  appointment_type: string;
  remarks: string;
  created_by_name: string;
}

interface EmploymentHistoryListProps {
  history: EmploymentRecord[];
  onEdit?: (record: EmploymentRecord) => void;
  editable?: boolean;
}

const EmploymentHistoryList: React.FC<EmploymentHistoryListProps> = ({
  history,
  onEdit,
  editable = false,
}) => {
  const theme = useTheme();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Present";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getAppointmentTypeColor = (type: string): any => {
    switch (type) {
      case "new_appointment":
        return "success";
      case "promotion":
        return "primary";
      case "transfer":
        return "warning";
      case "grade_change":
        return "secondary";
      default:
        return "default";
    }
  };

  const formatType = (type: string) => {
    return type.replace("_", " ").toUpperCase();
  };

  if (history.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No employment history records found.
        </Typography>
      </Box>
    );
  }

  // Sort history by start_date descending
  const sortedHistory = [...history].sort(
    (a, b) =>
      new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
  );

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      {sortedHistory.map((record) => (
        <Card
          key={record.id}
          variant="outlined"
          sx={{
            position: "relative",
            overflow: "visible",
            "&::before": !record.end_date
              ? {
                  content: '""',
                  position: "absolute",
                  left: -2,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  bgcolor: "success.main",
                  borderRadius: "4px 0 0 4px",
                }
              : {},
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="div" fontWeight="bold">
                  {record.designation_name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {record.department_name}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Chip
                  label={formatType(record.appointment_type)}
                  size="small"
                  color={getAppointmentTypeColor(record.appointment_type)}
                  variant="outlined"
                />
                {editable && onEdit && (
                  <Tooltip title="Edit Record">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(record)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Stack direction="row" spacing={3} sx={{ my: 1.5 }}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  PERIOD
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDate(record.start_date)} —{" "}
                  {formatDate(record.end_date)}
                  {!record.end_date && (
                    <Chip
                      label="CURRENT"
                      size="small"
                      color="success"
                      sx={{ ml: 1, height: 20, fontSize: "0.65rem" }}
                    />
                  )}
                </Typography>
              </Box>
              {record.grade_name && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    GRADE
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {record.grade_name}
                  </Typography>
                </Box>
              )}
            </Stack>

            {record.remarks && (
              <>
                <Divider sx={{ my: 1, borderStyle: "dashed" }} />
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", color: "text.secondary" }}
                >
                  "{record.remarks}"
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
};

export default EmploymentHistoryList;
